import { Power, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';

import { BusinessLogo } from '@/components/custom/business-logo';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useUserLogoutMutation } from '@/features/auth/api/useUsersLogoutMutation';
import { useStore } from '@/stores';

type QuickLogoutProps = {
  onSwitchBusiness?: () => void;
  isMainGroup?: boolean;
  isSelectedMain?: boolean;
};

const QuickLogout = ({ onSwitchBusiness, isMainGroup, isSelectedMain }: QuickLogoutProps) => {
  const userProfile = useStore((state) => state.profile);
  const selectedBusiness = useStore((state) => state.selectedBusiness);
  const accessToken = useStore.getState().accessToken;

  const displayName = selectedBusiness?.name || userProfile?.business.name || '-';
  const businessLabel =
    userProfile?.is_group === true && selectedBusiness != null
      ? isSelectedMain
        ? 'Main Business'
        : 'Sub Business'
      : 'Main Business';

  const { mutate } = useUserLogoutMutation({
    onSuccess: () => {
      localStorage.removeItem('dashboard-merchant-storage');
      window.location.replace('/auth/login');
    },
    onError: (err) => {
      toast.error('Logout is failed', { description: err.message });
    },
  });

  const signOut = async () => {
    if (!accessToken) return;
    mutate({ access_token: accessToken });
  };

  return (
    <div className="sticky bottom-0 p-4">
      <div className="flex flex-row items-center justify-between rounded-lg bg-blue-50 p-4">
        <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
          <div
            data-testid="sidebar-avatar-business-logo"
            className="h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-white"
          >
            {userProfile ? (
              <BusinessLogo model={userProfile.business.logo_path} />
            ) : (
              <Avatar className="h-full w-full">
                <AvatarImage src={'/images/profile/user-1.jpg'} />
                <AvatarFallback>doitpay</AvatarFallback>
              </Avatar>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs text-gray-500">{businessLabel}</div>
            <div className="truncate font-bold text-black">{displayName}</div>
            <div className="truncate text-sm text-gray-500">{userProfile?.name || '-'}</div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {isMainGroup && onSwitchBusiness && (
            <button
              onClick={onSwitchBusiness}
              className="rounded-md p-2 transition-colors hover:bg-blue-100"
              aria-label="Switch business"
            >
              <ChevronsUpDown className="text-brand-blue h-4 w-4" />
            </button>
          )}
          <button
            className="rounded-md p-2 transition-colors hover:bg-blue-100"
            onClick={signOut}
            aria-label="Logout"
          >
            <Power className="text-brand-blue h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickLogout;
